/**
 * OHLC bar engine — TradingView Lightweight Charts compliant updates.
 *
 * Practices (see https://tradingview.github.io/lightweight-charts/tutorials/demos/realtime-updates):
 *  - series.update(bar) for the current (forming) bar and for appending the next bar
 *  - series.setData(bars) only for full history load / resync
 *  - Never create duplicate times; never leave incomplete OHLC (open always set)
 *  - Tick / LTP input is folded into session-aligned buckets (NSE 09:15 grid)
 *
 * Board rows use `date` as the chart time key (unix + IST offset used by the SPA).
 * LWC series points use `time`.
 */

/** NSE cash/index regular session (IST minutes from midnight). */
export const NSE_SESSION = {
  OPEN_MIN: 9 * 60 + 15,   // 09:15
  /** Exclusive session end — no bar may open at or after this. */
  CLOSE_MIN: 15 * 60 + 30, // 15:30
};

/** Last valid bar-open minute for interval (1m→15:29, 5m→15:25). */
export function lastBarOpenMins(intervalMinutes = 5) {
  const step = Math.max(1, Number(intervalMinutes) || 5);
  const { OPEN_MIN, CLOSE_MIN } = NSE_SESSION;
  return OPEN_MIN + Math.floor((CLOSE_MIN - OPEN_MIN - 1) / step) * step;
}

/**
 * True when HH:MM (or minutes-from-midnight) is a valid bar open for the interval.
 */
export function isSessionAlignedTime(timeOrMins, intervalMinutes = 5) {
  let mins;
  if (typeof timeOrMins === 'number') {
    mins = timeOrMins;
  } else {
    const m = String(timeOrMins || '').match(/^(\d{1,2}):(\d{2})/);
    if (!m) return false;
    mins = Number(m[1]) * 60 + Number(m[2]);
  }
  const step = Math.max(1, Number(intervalMinutes) || 5);
  const open = NSE_SESSION.OPEN_MIN;
  const last = lastBarOpenMins(step);
  if (mins < open || mins > last) return false;
  return ((mins - open) % step) === 0;
}

/**
 * Floor a Date (or epoch ms) to the session-aligned bar open (IST).
 * Returns null outside regular session (at/after 15:30) — never invent night bars.
 * @param {number|Date} ts
 * @param {number} intervalMinutes
 * @returns {{ date: string, label: string, chartTime: number }|null}
 */
export function sessionBucket(ts, intervalMinutes = 5) {
  const d = typeof ts === 'number' ? new Date(ts) : ts;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '0';
  const y = get('year');
  const m = get('month');
  const day = get('day');
  const h = Number(get('hour'));
  const min = Number(get('minute'));
  const mins = h * 60 + min;
  const open = NSE_SESSION.OPEN_MIN;
  const close = NSE_SESSION.CLOSE_MIN;
  const step = Math.max(1, Number(intervalMinutes) || 5);

  // Hard stop: do not create/update bars at or after official close.
  if (mins >= close) return null;

  const last = lastBarOpenMins(step);
  let fromOpen;
  if (mins < open) {
    fromOpen = 0;
  } else {
    fromOpen = Math.floor((mins - open) / step) * step;
    if (open + fromOpen > last) fromOpen = last - open;
  }

  const total = open + fromOpen;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  const date = `${y}-${m}-${day}`;
  const label = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;

  let chartTime = 0;
  try {
    const wall = new Date(`${date}T${label}:00+05:30`);
    if (!Number.isNaN(wall.getTime())) {
      // Match SPA convention: IST wall clock exposed as UTC getters (+19800)
      chartTime = Math.floor(wall.getTime() / 1000) + 19800;
    }
  } catch (_) { /* noop */ }

  if (!chartTime) return null;
  return { date, label, chartTime };
}

/**
 * Filter chart candles to NSE session opens. Chart times use IST-as-UTC (+19800).
 * @param {Array<{date?:number,time?:number,label?:string}>} candles
 * @param {number} intervalMinutes
 */
export function filterSessionCandles(candles, intervalMinutes = 1) {
  if (!Array.isArray(candles) || !candles.length) return [];
  const step = Math.max(1, Number(intervalMinutes) || 1);
  return candles.filter((c) => {
    if (!c) return false;
    if (c.label && isSessionAlignedTime(c.label, step)) return true;
    const t = Number(c.date ?? c.time);
    if (!t || !Number.isFinite(t)) return false;
    // SPA stores IST wall as UTC getters → getUTC* yields IST wall clock.
    const d = new Date(t * 1000);
    const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
    return isSessionAlignedTime(mins, step);
  });
}

/**
 * Apply a single trade/LTP tick into a forming OHLC bar.
 * Pure — returns a new bar object.
 *
 * @param {{ time: number, open: number, high: number, low: number, close: number, volume?: number }|null} prev
 * @param {number} price
 * @param {number} chartTime  bar open (LWC time)
 * @param {number} [volumeDelta]
 */
export function applyTickToBar(prev, price, chartTime, volumeDelta = 0) {
  const px = Number(price);
  if (!(px > 0) || !chartTime) return prev;

  if (prev && prev.time === chartTime) {
    return {
      time: chartTime,
      open: Number(prev.open),
      high: Math.max(Number(prev.high), px),
      low: Math.min(Number(prev.low), px),
      close: px,
      volume: Number(prev.volume || 0) + Number(volumeDelta || 0),
    };
  }

  // New bar: open from previous close when available (gapless series)
  const open = prev && Number(prev.close) > 0 ? Number(prev.close) : px;
  return {
    time: chartTime,
    open,
    high: Math.max(open, px),
    low: Math.min(open, px),
    close: px,
    volume: Number(volumeDelta || 0),
  };
}

/**
 * Merge an authoritative server candle into a board-row series.
 * Board rows: { date, open, high, low, close, volume? }
 * - Same time → update H/L/C (keep open from first print if server open missing)
 * - Newer time → append
 * - Older time → ignore (no out-of-order mutation)
 *
 * @param {Array<object>} prevCandles
 * @param {object} row  { date|time, open, high, low, close, volume? }
 * @returns {Array<object>}
 */
export function mergeBarSeries(prevCandles, row) {
  if (!row) return prevCandles || [];
  const time = Number(row.date ?? row.time);
  if (!time || !Number.isFinite(time)) return prevCandles || [];

  const bar = {
    date: time,
    open: Number(row.open),
    high: Number(row.high),
    low: Number(row.low),
    close: Number(row.close),
    volume: Number(row.volume || 0),
  };
  if (!(bar.close > 0) && !(bar.open > 0)) return prevCandles || [];
  // Normalize incomplete OHLC
  if (!(bar.open > 0)) bar.open = bar.close;
  if (!(bar.high > 0)) bar.high = Math.max(bar.open, bar.close);
  if (!(bar.low > 0)) bar.low = Math.min(bar.open, bar.close);
  bar.high = Math.max(bar.high, bar.open, bar.close);
  bar.low = Math.min(bar.low, bar.open, bar.close);

  if (!prevCandles?.length) return [bar];

  const next = prevCandles.slice();
  const last = next[next.length - 1];
  const lastT = Number(last.date ?? last.time);

  if (lastT === bar.date) {
    next[next.length - 1] = {
      ...last,
      // Prefer first open of the bar (server should keep it stable)
      open: Number(last.open) > 0 ? Number(last.open) : bar.open,
      high: Math.max(Number(last.high), bar.high),
      low: Math.min(Number(last.low) || bar.low, bar.low),
      close: bar.close,
      volume: bar.volume || last.volume || 0,
    };
    return next;
  }

  if (bar.date > lastT) {
    next.push(bar);
    return next;
  }

  // Older than last → ignore for live path (avoids flicker / reverse writes)
  return prevCandles;
}

/**
 * Apply candles to a Lightweight Charts series with minimal flicker.
 *
 * @param {import('lightweight-charts').ISeriesApi<'Candlestick'>} series
 * @param {Array<{ time: number, open: number, high: number, low: number, close: number }>} points
 * @param {object} state  mutable refs: { len, lastTime, lastPoint, didFit }
 * @param {object} [opts]
 * @param {boolean} [opts.live]
 * @param {import('lightweight-charts').IChartApi} [opts.chart]
 * @param {(a: object, b: object) => boolean} [opts.pointChanged]
 * @returns {{ mode: 'update'|'set'|'skip' }}
 */
/**
 * Sanitize LWC candle points — drop invalid / non-monotonic bars so setData never throws.
 * @param {Array<{time:any,open:number,high:number,low:number,close:number}>} points
 */
export function sanitizeLwcPoints(points) {
  if (!Array.isArray(points) || !points.length) return [];
  const out = [];
  let lastTime = null;
  for (const p of points) {
    if (!p || p.time == null || p.time === '') continue;
    const open = Number(p.open);
    const high = Number(p.high);
    const low = Number(p.low);
    const close = Number(p.close);
    if (![open, high, low, close].every((n) => Number.isFinite(n))) continue;
    // LWC requires high >= low; repair minor inversions
    const hi = Math.max(high, low, open, close);
    const lo = Math.min(high, low, open, close);
    const time = p.time;
    // Skip duplicate / out-of-order times (keep last write for same time)
    if (lastTime != null) {
      const same = String(time) === String(lastTime);
      const older = typeof time === 'number' && typeof lastTime === 'number'
        ? time < lastTime
        : String(time) < String(lastTime);
      if (older) continue;
      if (same && out.length) {
        out[out.length - 1] = { time, open, high: hi, low: lo, close };
        continue;
      }
    }
    out.push({ time, open, high: hi, low: lo, close });
    lastTime = time;
  }
  return out;
}

export function applySeriesPoints(series, points, state, opts = {}) {
  if (!series || !points?.length) return { mode: 'skip' };

  const clean = sanitizeLwcPoints(points);
  if (!clean.length) return { mode: 'skip' };

  const live = !!opts.live;
  const lastPoint = clean[clean.length - 1];
  const lastTime = lastPoint?.time;
  const prevLen = state.len || 0;
  const prevTime = state.lastTime;
  const prevPoint = state.lastPoint;

  const changed = opts.pointChanged || ((a, b) => {
    if (!a || !b) return true;
    return a.time !== b.time
      || a.open !== b.open
      || a.high !== b.high
      || a.low !== b.low
      || a.close !== b.close;
  });

  const canIncremental = live && prevLen > 0 && prevTime != null;

  try {
    // Same bar count, same last time → update forming bar only
    if (canIncremental && clean.length === prevLen && lastTime === prevTime) {
      if (changed(prevPoint, lastPoint)) {
        series.update(lastPoint);
        state.lastPoint = lastPoint;
      }
      state.len = clean.length;
      state.lastTime = lastTime;
      return { mode: 'update' };
    }

    // Exactly one new bar → append via update (LWC appends when time is newer)
    if (canIncremental && clean.length === prevLen + 1 && lastTime > prevTime) {
      series.update(lastPoint);
      state.lastPoint = lastPoint;
      state.len = clean.length;
      state.lastTime = lastTime;
      return { mode: 'update' };
    }

    // Multi-bar catch-up while live: update only the last bar if history prefix matches
    if (
      canIncremental
      && clean.length > prevLen + 1
      && prevPoint
      && clean[prevLen - 1]
      && clean[prevLen - 1].time === prevTime
    ) {
      for (let i = prevLen; i < clean.length; i++) {
        series.update(clean[i]);
      }
      state.lastPoint = lastPoint;
      state.len = clean.length;
      state.lastTime = lastTime;
      return { mode: 'update' };
    }

    // Full replace (initial load, TF change, history rewrite)
    series.setData(clean);
  } catch (err) {
    // Last resort: full setData after sanitize, then reset incremental state
    try {
      series.setData(clean);
    } catch (err2) {
      try { console.warn('[TMR] applySeriesPoints failed', err2); } catch (_) {}
      return { mode: 'skip' };
    }
  }

  state.lastPoint = lastPoint;
  state.len = clean.length;
  state.lastTime = lastTime;
  if (opts.chart && !state.didFit) {
    try {
      opts.chart.timeScale().fitContent();
    } catch (_) { /* noop */ }
    state.didFit = true;
  }
  return { mode: 'set' };
}

/**
 * Board-row → LWC candlestick point.
 * @param {{ date: number|string, open: number, high: number, low: number, close: number }} d
 */
export function toLwcPoint(d) {
  if (!d) return null;
  return {
    time: d.date ?? d.time,
    open: Number(d.open),
    high: Number(d.high),
    low: Number(d.low),
    close: Number(d.close),
  };
}
